import { useEffect, useState, useRef } from "react";
import { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import axiosConfig from "../utils/axiosConfig";

export interface UseFetchState<T> {
  data: T | null;
  loading: boolean;
  error: AxiosError | null;
}

const useFetch = <T>(
  options: AxiosRequestConfig<any> | null,
): UseFetchState<T> => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError | null>(null);
  const [data, setData] = useState<T | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!options) {
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const currentController = new AbortController();
    abortControllerRef.current = currentController;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response: AxiosResponse<any> = await axiosConfig({
          ...options,
          signal: currentController.signal,
        });

        if (response.data?.status && response.data.status === "error") {
          throw new AxiosError(
            response.data.message ?? "Something went wrong!",
          );
        }

        setData(response.data);
      } catch (error) {
        if (error instanceof AxiosError) {
          if (
            error.name === "AbortError" ||
            error.name === "CanceledError" ||
            error.code === "ERR_CANCELED" ||
            error.message?.includes("canceled") ||
            error.message?.includes("aborted")
          ) {
            return;
          }
        }

        console.error("API Error:", error);
        setError(error as AxiosError);
      } finally {
        if (abortControllerRef.current === currentController) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      currentController.abort();
    };
  }, [JSON.stringify(options)]);

  return { loading, error, data };
};

export default useFetch;
